import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { buildHead, breadcrumbJsonLd } from "@/lib/seo";
import { loadPublicResourcesForHub } from "@/lib/query-registry";
import { ResourceHub, collectionPageJsonLd } from "@/components/site/ResourceHub";
import { SITE_URL } from "@/lib/i18n";
import coverPerfEs from "@/assets/cover-pillar-performance-es.jpg";
import { setEdgeHtmlCache } from "@/lib/cache-headers";

const PATH = "/es/recursos/rendimiento";
const ALT = "/resources/performance";
const FIELD_NOTE = "/es/recursos/regresion-lcp-36-urls";
const TITLE = "Recursos y playbooks de rendimiento Shopify · Shugert";
const DESC =
  "Playbooks de velocidad Shopify y field notes transparentes sobre LCP, INP, CLS, apps, imágenes y Core Web Vitals.";

export const Route = createFileRoute("/es/recursos/rendimiento")({
  loader: async ({ context }) => {
    await setEdgeHtmlCache();
    return loadPublicResourcesForHub(context.queryClient);
  },
  head: ({ loaderData }) => {
    const resources = (loaderData as any)?.resources ?? [];
    const urls = resources
      .filter((r: any) =>
        ((r.tags_es?.length ? r.tags_es : r.tags_en) ?? []).join(" ").toLowerCase()
          .match(/performance|rendimiento|velocidad|core web vitals|lcp|inp|cls/),
      )
      .map((r: any) => `${SITE_URL}/es/recursos/${r.slug_es ?? r.slug}`);
    urls.push(`${SITE_URL}${FIELD_NOTE}`);
    return buildHead({
      locale: "es",
      title: TITLE,
      description: DESC,
      path: PATH,
      altPath: ALT,
      ogImage: coverPerfEs,
      jsonLd: [
        breadcrumbJsonLd([
          { name: "Inicio", path: "/es" },
          { name: "Recursos", path: "/es/recursos" },
          { name: "Rendimiento", path: PATH },
        ]),
        collectionPageJsonLd("Recursos de Rendimiento Shopify", `${SITE_URL}${PATH}`, DESC, urls),
      ],
    });
  },
  component: () => (
    <SiteShell>
      <ResourceHub
        locale="es"
        cluster="performance"
        copy={{
          eyebrow: "Recursos de rendimiento",
          title: "Recursos de rendimiento Shopify: playbooks, checklists y notas de campo.",
          intro:
            "Empieza con la guía completa y usa los recursos específicos para investigar apps, imágenes, Core Web Vitals y regresiones de release. Separamos claims de validación de campo para que el contenido siga siendo útil después del titular.",
          serviceLabel: "Ver Shopify Performance",
          serviceHref: "/es/rendimiento",
          articlesTitle: "Guías de rendimiento",
          articlesEmpty: "Más guías de rendimiento pronto. Mientras tanto, solicita una auditoría.",
          read: "Leer",
          min: "min",
          by: "Por",
          updated: "Actualizado",
          backLabel: "Recursos",
          backHref: "/es/recursos",
        }}
      />
      <section className="mx-auto -mt-24 max-w-7xl px-6 pb-28">
        <a
          href={FIELD_NOTE}
          className="block rounded-3xl border border-border bg-card p-8 transition-colors hover:border-foreground/30 sm:p-10"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Nuevo field note · Julio 2026</p>
          <h2 className="mt-3 max-w-3xl font-display text-2xl font-semibold leading-tight tracking-tight text-foreground sm:text-3xl">
            Cómo diagnosticamos una regresión de LCP en 36 URLs
          </h2>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            El grupo afectado, las causas compartidas que probamos, los cambios acotados y por qué desplegar no equivale a resolver un problema de campo en Search Console.
          </p>
          <span className="mt-6 inline-flex text-sm font-semibold text-foreground">Leer el field note →</span>
        </a>
      </section>
    </SiteShell>
  ),
});
